import React, {
  useEffect,
  useState,
  useReducer,
  useCallback,
  createContext,
  useContext,
  useRef,
  useMemo
} from 'react';
import EmailDialogView from '../view/EmailDialogView';
import EmailNoDialogView from '../view/EmailNoDialogView';
import EmailSimpleView from '../view/EmailSimpleView';
import fetchAPI from '../../utils/fetchAPI';
import { FetchCallbacksContext } from '../../internal-components/FetchCallbacks';
import useTranslations from '../../hooks/useTranslations';

const EmailDialogReducer = (state, action) => {
  const { type, payload } = action;

  switch (type) {
    case 'update':
      return { ...state, ...payload };

    default:
      return state;
  }
};

export const EMAIL_DIALOG_TYPES = {
  COMPOSE_BLANK: 'blank', // plain email dialog, no text is placed for the user
  COMPOSE: 'preview', // send email only, a template is used
  TEMPLATE: 'template' // only edit template, send email is not avilable
};

const TAB_VALUES = {
  COMPOSE: 0,
  EDIT_TEMPLATE: 1,
  PREVIEW: 2,
  FREQUENCY: 3
};

export const VIEWS = {
  DIALOG: 'dialog',
  NO_DIALOG: 'no_dialog',
  SIMPLE: 'simple'
};

export const isFieldPlaceHolderAllowed = code => {
  return (
    code === 'followup_email' ||
    code === 'reallocate_admin' ||
    code === 'reallocate_end_user' ||
    code === 'share_submission' ||
    code === 'share_submission_admin' ||
    code === 'automation' ||
    code === 'assigned_to_review_submission'
  );
};

export const Context = createContext(null);
/**
 *
 * @param {Boolean} open - dialog open or closed
 * @param {fn} handleConfirm - function that being called on email sent successfully, it populates data with current reducer state and tId with templateId
 * @param {fn} handleClose - function responsible of closing the dialog
 * @param {fn} onCancel - function that is being called on cancel
 * @param {Boolean} isConfirmBtnDisabled - disable/enable send/save button
 * @param {String} description - optional: add description to the dialog
 * @param {String} templateId - id of the email template, if left blank - BLANK_COMPOSE mode will be applied
 * @param {fn} setTemplateId - // TODO: write decription
 * @param {String} assetId - id of the form for saving form-specific custom system emails
 * @param {String} assetType - eg 'forms' for form-specific custom system emails
 * @param {String} templateCode - a code necessary for getting form-specific custom system emails
 * @param {String} customUrl - // a custom URL that will be called when clicking "Send", instead of the default email end point.
 * @param {EMAIL_DIALOG_TYPES} type - which mode to open the dialog in
 * @param {fn} customBodyParsing - // TODO: write decription
 * @param {Array} placeHolders -  array of option for select cmp. should contain objects in the following structure: {text: "text to display in drop-down" value: <unique value>}
 * @param {Array} additionalPlaceHolders -  array of option for select cmp. should contain objects in the following structure: {text: "text to display in drop-down" value: <unique value>}
 * @param {Object} additionalPlaceHoldersProps - props for the select component used to insert additional place holders
 * @param {fn} reducerListener -  data => {...} will call this function every tine data has updated
 * @param {Array || String} to - array of objects that contains submissions/users ids to send the email to and their preview text to display in the dialog. should use the following structure: {id: <string:id>, email: <string:email>}. alternatively a string contains a query for the backend
 * @param {int} usersCount4FilterQuery - number of users cought in filters (used when <to> contains a query)
 * @param {boolean} aggregateEmailsDisplay - true if it should aggregate the emails list or false to show the full list. true: "example@vinciworks.com", "and X more" | false: "example.0@vinciworks.com", "example.1@vinciworks.com"...
 * @param {boolean} readOnlyEmailsList - allow users to edit emails (to) list or not. note: will be set to true automatically if <to> prop is passed
 * @param {String} formId4ReminderEmail - this influences the API endpoint. we can pass a form id here to make use of the API endpoint responsible of sending a reminder email. this comes into play when we want to populate the "latest reminder" column under the invite only form incomplete submissions grid
 * @param {String} formId4RequestingInfo - this influences the API endpoint. we can pass a form id here to make use of the request more info endpoint, which has other effects
 * @param {boolean} allowNewTemplateCreation - // TODO: write decription
 * @param {boolean} forceNewTemplateCreation - on save will not override the original template
 * @param {Object} editTemplateProps - props to pass to EditEmailTemplateEngine
 * @param {Object} composeEmailProps - props to pass to ComposeEmailEngine
 * @param {Boolean} noSubject - if true, the subject input will not be used
 * @param {Boolean} noTo - if true, the to input will not be used
 * @param {Boolean} noCC - if true, the CC input will not be used
 * @param {Boolean} view - choose which vew to use with the engine (by default is dialog) options avilable in EmailDialogEngine.VIEWS
 * @param {Node} topEndPoint - stuff to put below the tabs
 * @param {Object} additionalSaveTemplateBodyParams - additional params to include in the body of save template call
 * @param {Boolean} showFrequencyTab - if true, the frequency tab will be included
 */
const EmailDialogEngine = ({
  open,
  handleConfirm = (data, tId) => data,
  handleClose = () => {},
  onCancel: givenOnCancel = () => {},
  isConfirmBtnDisabled,
  description = '',
  templateId = '',
  setTemplateId = v => v,
  assetId = '',
  assetType = '',
  templateCode = '',
  customUrl,
  type = EMAIL_DIALOG_TYPES.COMPOSE_BLANK,
  customBodyParsing = b => b,
  placeHolders: givenPlaceHolders,
  additionalPlaceHolders,
  additionalPlaceHoldersProps,
  reducerListener,
  to = [],
  usersCount4FilterQuery = 0,
  aggregateEmailsDisplay,
  readOnlyEmailsList,
  formId4ReminderEmail = '',
  formId4RequestingInfo = '',
  allowNewTemplateCreation,
  forceNewTemplateCreation,
  editTemplateProps,
  composeEmailProps,
  previewEmailProps,
  noSubject,
  noTo,
  noCC,
  view = VIEWS.DIALOG,
  topEndPoint,
  additionalSaveTemplateBodyParams = {},
  showFrequencyTab,
  ...generalDialogProps
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [initLoader, setInitLoader] = useState(true); // enabled after first api call to allow load of data properly
  const [activeTab, setActiveTab] = useState(TAB_VALUES.COMPOSE);
  const [listReplacement, setListReplacement] = useState({});
  const [placeHolders, setPlaceHolders] = useState(givenPlaceHolders || []);
  const [dataType, setDataType] = useState('USER_EMAIL'); //TODO: verify this // used in send email API
  const dialogHandleConfirm = useRef(() => {});
  const [data, dispatch] = useReducer(EmailDialogReducer, {
    emails: [],
    cc: [],
    subject: '',
    body: '',
    subjectInEdit: '',
    bodyInEdit: '',
    code: null
  });

  // console.log('data', data);

  const { openSnackbar } = useContext(FetchCallbacksContext);
  const t = useTranslations();

  useEffect(() => {
    if (reducerListener) {
      const body = customBodyParsing(frontEndReplacments(data.body, false));
      const parsedData = Object.assign({}, data, { body });
      reducerListener(parsedData);
    }
  }, [data]);

  useEffect(() => {
    let url, urlParams;
    if (!open && view === VIEWS.DIALOG) {
      return;
    }

    if (
      ((templateId && templateId.length) || (assetId && templateCode)) &&
      type !== EMAIL_DIALOG_TYPES.COMPOSE_BLANK
    ) {
      setInitLoader(true);
      setIsLoading(true);

      url = `/mailer/v1/emails/${templateId}`;

      if (assetId || assetType || templateCode) {
        url += '?';
        urlParams = [];
        if (assetId) {
          urlParams.push(`assetId=${assetId}`);
        }
        if (assetType) {
          urlParams.push(`assetType=${assetType}`);
        }
        if (templateCode) {
          urlParams.push(`code=${templateCode}`);
        }
        url += urlParams.join('&');
      }

      fetchAPI(url).then(res => {
        const data = res.results[0];
        const body = frontEndReplacments(data.body, true);
        dispatch({
          type: 'update',
          payload: {
            cc: [],
            body: body,
            subject: data.subject,
            bodyInEdit: body,
            subjectInEdit: data.subject,
            code: data.code
          }
        });
        if (!templateId) {
          setTemplateId(res.id);
        }
        setDataType(data.dataType);
        setIsLoading(false);
        setInitLoader(false);
      });
      setPlaceHoldersFromAPI();
    } else {
      // type = EMAIL_DIALOG_TYPES.COMPOSE_BLANK;
      // clear previous data and init state to default state
      dispatch({
        type: 'update',
        payload: {
          emails: [],
          cc: [],
          subject: '',
          body: '',
          subjectInEdit: '',
          bodyInEdit: '',
          code: null
        }
      });
      setInitLoader(false);
      setIsLoading(false);
    }

    type === EMAIL_DIALOG_TYPES.TEMPLATE &&
      setActiveTab(TAB_VALUES.EDIT_TEMPLATE);
  }, [fetchAPI, open, templateId]);

  const sortPlaceholdersFunc = (a, b) => {
    const lcDescriptionA = (a?.description || '').toLowerCase();
    const lcDescriptionB = (b?.description || '').toLowerCase();
    if (lcDescriptionA < lcDescriptionB) {
      return -1;
    }
    if (lcDescriptionA > lcDescriptionB) {
      return 1;
    }
    return 0;
  };

  const setPlaceHoldersFromAPI = useCallback(() => {
    if (givenPlaceHolders && givenPlaceHolders.length) {
      return;
    }

    const frontEndPlaceholders = [];
    if (
      templateId === '00000000-0000-0000-0000-000000000002' ||
      templateId === '00000000-0000-0000-0000-000000000004'
    ) {
      frontEndPlaceholders.push({
        text: 'Reset password link',
        value: 'resetPasswordUrlHtml',
        'data-testid': `placeholder-`,
        description: t(`email_template.placeholders.resetPasswordLink`)
      });
    }
    if (templateId === '00000000-0000-0000-0000-000000000010') {
      frontEndPlaceholders.push({
        text: 'Activate account link',
        value: 'activateUrlHtml',
        'data-testid': `placeholder-`,
        description: t(`email_template.placeholders.activateAccountLink`)
      });
    }

    fetchAPI(`/mailer/v1/emails/${templateId}/placeholders?novalues=true`).then(
      r => {
        const receivedPlaceHolders = r.elements;
        const options = receivedPlaceHolders
          .map(pH => ({
            text: pH,
            value: pH,
            'data-testid': `placeholder-${pH}`,
            description: t(`email_template.placeholders.${pH}`)
          }))
          .concat(frontEndPlaceholders)
          .sort(sortPlaceholdersFunc);

        setPlaceHolders(options);
      }
    );
  }, [fetchAPI, placeHolders, setPlaceHolders, templateId]);

  const onEmailSend = useCallback(() => {
    let url = formId4ReminderEmail.length
      ? `/forms/v1/forms/${formId4ReminderEmail}/submissions/bulk/reminder`
      : formId4RequestingInfo
      ? `/forms/v1/forms/${formId4RequestingInfo}/submissions/bulk/moreinfo`
      : '/mailer/v1/emails/sendNoTemplate';

    //Override the default end point with a custom URL.
    if (customUrl) {
      url = customUrl;
    }
    let params = {};

    if (data.toUserIds) {
      const idArray = to.map(addressNode => addressNode.id);
      params = {
        toUserIds: data.toUserIds,
        to: '${to}',
        data: [{ ids: { idArray, idType: dataType } }]
      };
    } else if (to.length) {
      if (Array.isArray(to)) {
        const idArray = to.map(addressNode => addressNode.id);
        params.data = [{ ids: { idArray, idType: dataType } }];
      } else {
        params.data = [{ ids: { idArray: [], query: to, idType: dataType } }];
      }
      params.to = dataType === 'USER_EMAIL' ? '${info.email}' : '${to}';
    } else {
      // if we didn't get email addresses it means the addresses came from the user therefore we want to use those valus
      params.data = [];
      params.to = data.emails.join();
    }
    params.emailContent = customBodyParsing(
      frontEndReplacments(data.body, false)
    );
    if (params.emailContent.match(/\$\{f\d/)) {
      openSnackbar('error', 'snackbars.emailResponse.illegal_placeholders');
      return;
    }
    params.emailSubject = data.subject;
    params.cc = data.cc.join(); // ["a", "b", "c"] -> "a,b,c

    setIsLoading(true);
    fetchAPI(url, {
      method: 'POST',
      body: params
    })
      .then(() => {
        handleClose();
        openSnackbar('success', 'snackbars.emailResponse.success');
        handleConfirm(data, templateId);
      })
      .catch(e => {
        openSnackbar('error', 'snackbars.emailResponse.failure');
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [data, formId4ReminderEmail, to]);

  const onTemplateSave = useCallback(() => {
    dispatch({
      type: 'update',
      payload: {
        body: data.bodyInEdit,
        subject: data.subjectInEdit
      }
    });

    let url = `/mailer/v1/emails/${templateId}`;
    let method = 'PUT';
    if (
      (allowNewTemplateCreation && !isNaN(templateId.replace(/-/g, ''))) ||
      forceNewTemplateCreation
    ) {
      // check if template id contains only numbers
      // if we got here this is not a custom template
      url = `/mailer/v1/emails`;
      method = 'POST';
    }

    let body = {
      body: customBodyParsing(frontEndReplacments(data.bodyInEdit, false)),
      subject: data.subjectInEdit,
      code: data.code,
      id: templateId
    };

    if (assetId) {
      body.assetId = assetId;
    }

    if (assetType) {
      body.assetType = assetType;
    }

    body = { ...body, ...additionalSaveTemplateBodyParams };

    setIsLoading(true);
    fetchAPI(url, {
      body: body,
      method,
      handleResponse: r => r.headers.map.location
    })
      .then(location => {
        const newTemplateId = location
          ? location.replace('mailer/', '')
          : templateId;
        setTemplateId(newTemplateId);

        handleClose();
        openSnackbar('success', 'snackbars.edit_template.success');
        handleConfirm(data, newTemplateId);
      })
      .catch(e => {
        openSnackbar('error', 'snackbars.edit_template.error');
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [
    data,
    templateId,
    setTemplateId,
    handleConfirm,
    allowNewTemplateCreation,
    forceNewTemplateCreation
  ]);

  const onCancel = useCallback(() => {
    // is dirty?
    dispatch({
      type: 'update',
      payload: {
        emails: [],
        cc: [],
        subject: '',
        body: '',
        subjectInEdit: '',
        bodyInEdit: '',
        code: null,
        toUserIds: null
      }
    });
    givenOnCancel();
    handleClose();
  }, [data, activeTab, handleClose, givenOnCancel]);

  const buttons = useMemo(() => {
    const _buttons = [
      {
        text: 'dialog.cancel',
        variant: 'outlined',
        onClick: onCancel
      }
    ];
    let getButtonsOfTab = activeTab;
    if (activeTab === TAB_VALUES.PREVIEW) {
      if (type === EMAIL_DIALOG_TYPES.TEMPLATE) {
        getButtonsOfTab = TAB_VALUES.EDIT_TEMPLATE;
      } else {
        getButtonsOfTab = TAB_VALUES.COMPOSE;
      }
    } 
    switch (getButtonsOfTab) {
      case TAB_VALUES.COMPOSE:
          _buttons.push({
          text: 'email_dialog.send',
          onClick: 'confirm',
          disabled: isConfirmBtnDisabled
        });
        dialogHandleConfirm.current = onEmailSend;
        break;
      case TAB_VALUES.EDIT_TEMPLATE:
        _buttons.push({
          text: 'email_template.save',
          onClick: onTemplateSave,
          disabled: isConfirmBtnDisabled
        });
        break;
      case TAB_VALUES.FREQUENCY:
        _buttons.push({
          text: 'email_template.save',
          onClick: onTemplateSave,
          disabled: isConfirmBtnDisabled
        });
        break;
      default:
        break;
    }

    return _buttons;
  }, [activeTab, data, onTemplateSave, isConfirmBtnDisabled, type]);

  const frontEndReplacments = useCallback(
    (body, incoming) => {
      const remindersListDefaultPlaceHolder = '${link to submission}';
      const backEndTranslations = {
        // add here back-end variables that should be translated for the ui
        deepLinks: 'submissions',
        children: 'submissions'
      };
      let regex,
        regexGroups,
        parsedLine,
        translatedList = 'stuff';

      if (incoming) {
        body = body.replace(
          /<a href=\${resetPasswordUrl}>Click here to set your password<\/a>/g,
          '${resetPasswordUrlHtml}'
        );

        body = body.replace(
          /<a href=\${resetPasswordUrl}>Click here to activate your account<\/a>/g,
          '${activateUrlHtml}'
        );

        body = body.replace(
          '<br><#if numberOfSubmissions gt maxLinksPerReminder>${linkAndMore}</#if>',
          ''
        );
        regex = /<#list (\w+)[\s\S]*<\/#list>/;
        regexGroups = regex.exec(body);
        if (regexGroups && backEndTranslations[regexGroups[1]]) {
          // get list name from translation
          translatedList = backEndTranslations[regexGroups[1]];

          let backEndLists = listReplacement;
          backEndLists[translatedList] = regexGroups[1];
          setListReplacement(Object.assign({}, backEndLists));
        } else {
          // no need to replace list of submissions
          if (regexGroups && regexGroups.length > 0) {
            return genericFrontEndReplacments(body, incoming);
          }
          return body;
        }

        regex = /<#list [\s\S]*<a href[^>]*>([\s\S]*)<\/a>[\s\S]*<\/#list>/g;
        regexGroups = regex.exec(body);
        if (regexGroups) {
          // found <a ...> element, means that this is custom parsing
          const customLine = regexGroups[1];
          parsedLine = customLine.replace(
            /\${child\.questions\.(f\d+|referenceId)\.question}/gi,
            ''
          ); // O20-2400 - for backwards compatibility

          while (regexGroups) {
            regex = /\${child\.questions\.(f\d+|referenceId)\.answer}/g;
            regexGroups = regex.exec(parsedLine);
            if (regexGroups) {
              // we found usage of questions placeholders
              parsedLine = parsedLine.replace(
                regexGroups[0],
                `\${${regexGroups[1]}}`
              );
            }
          }
        } else {
          parsedLine = `${remindersListDefaultPlaceHolder}`;
        }

        body = body.replace(
          /<#list [\s\S]*<\/#list>/g,
          `<b><i>\${list of ${translatedList} start - enter field placeholders below this line}</i></b><br><p>${parsedLine}</p><b><i>\${list of ${translatedList} end - enter field placeholders above this line}</i></b>`
        );
      } else {
        body = body.replace(/\$\{resetPasswordLink\}/g, '${resetPasswordUrl}>');

        regex =
          /(?:<strong>)?(?:<em>)?\${list of (\w*) start - enter field placeholders below this line}(?:<\/em>)?(?:<\/strong>)?(?:(?:(?:.*?)<br>(?: )?(?:<\/\w*>)*)|(?:<br>))?([\s\S]*)(?:<br>)(?: )*(?:<strong>)?(?:<em>)?(?: )*\${list of \w* end - enter field placeholders above this line}(?:<\/em>)?(?:<\/strong>)?/g;
        regexGroups = regex.exec(body);
        let listName = '';
        let userLine = '';
        if (
          regexGroups &&
          regexGroups.length > 2 &&
          listReplacement[regexGroups[1]]
        ) {
          listName = listReplacement[regexGroups[1]];
          userLine = regexGroups[2];
        } else {
          return genericFrontEndReplacments(body, incoming);
        }

        const questions = userLine.match(/\${((f\d+)|(referenceId))}/gi);

        let parsedList = '';
        if (questions) {
          // we have user fields, need to prepare if
          const questionsIds = questions.map(q => {
            return q.match(/(f\d+)|(referenceId)/gi)[0] || '';
          });
          questions.forEach((q, i) => {
            console.log('Before, userLine=', userLine);
            userLine = userLine.replace(
              q,
              `\${child.questions.${questionsIds[i]}.answer}`
            );
            console.log('After, userLine=', userLine);
          });
          const ifIds = questionsIds
            .map(id => ` && child.questions.${id}??`)
            .join('');
          const typeOfChildUrl =
            data.code === 'assigned_to_review_submission' ||
            data.code === 'share_submission_admin'
              ? 'adminUrl'
              : 'url';
          const ifElseStatement = `<#if child.questions?? ${ifIds}><a href="\${child.${typeOfChildUrl}}"> ${userLine} </a><#else><p> ${remindersListDefaultPlaceHolder}</p> </#if> `;
          parsedList = `<#list ${listName} as child>${ifElseStatement} <br></#list>`;
        } else {
          parsedList = `<#list ${listName} as child> <p> ${userLine}</p></#list>`;
        }
        body = body.replace(regexGroups[0], parsedList);
        const typeOfLink =
          data.code === 'assigned_to_review_submission' ||
          data.code === 'share_submission_admin'
            ? 'adminHref'
            : 'formUrlLink';
        body = body.replace(
          remindersListDefaultPlaceHolder,
          '${child.' + typeOfLink + '}'
        );
      }
      return body;
    },
    [listReplacement, data]
  );

  const genericFrontEndReplacments = useCallback(
    (body, incoming) => {
      const backEndTranslations = {
        // add here back-end variables that should be translated for the ui
        deepLinks: 'submissions',
        children: 'submissions'
      };
      let regex, regexGroups;
      if (incoming) {
        regex = /<#list (\w*)(.|\b|\n)*<\/#list>/;
        regexGroups = regex.exec(body);

        if (regexGroups && regexGroups.length > 1) {
          let backEndLists = listReplacement;

          if (backEndTranslations[regexGroups[1]]) {
            // if there is a replacment of variables to be made
            regexGroups[1] = backEndTranslations[regexGroups[1]];
          }

          let key = regexGroups[1];
          let value = regexGroups[0];
          backEndLists[key] = value;
          setListReplacement(Object.assign({}, backEndLists));
          body = body.replace(regex, '${list of ' + regexGroups[1] + '}');
        }
      } else {
        // add missing <table> if necessary
        body = body.replace(
          /\${list of questions}/g,
          '<table id="submission" class="submission">${list of questions}</table>'
        );
        body = body.replace(
          /<table id="submission" class="submission"><table id="submission" class="submission">/,
          '<table id="submission" class="submission">'
        );
        body = body.replace(/<\/table><\/table>/, '</table>');

        regex = /\${list of ([^}]*)}/;
        regexGroups = regex.exec(body);
        if (
          regexGroups &&
          regexGroups.length > 1 &&
          listReplacement[regexGroups[1]]
        ) {
          body = body.replace(regex, listReplacement[regexGroups[1]]);
        }
      }
      return body;
    },
    [listReplacement]
  );

  const contextProps = {
    isLoading,
    initLoader,
    data,
    dispatch,
    placeHolders,
    additionalPlaceHolders,
    additionalPlaceHoldersProps,
    noSubject,
    noCC,
    noTo,
    topEndPoint
  };

  const viewProps = {
    TAB_VALUES,
    open,
    handleClose,
    buttons,
    type,
    activeTab,
    setActiveTab,
    description,
    isLoading,
    data,
    dispatch,
    templateId,
    dialogHandleConfirm: dialogHandleConfirm.current,
    editTemplateProps,
    composeEmailProps: {
      readOnlyEmailsList,
      to,
      usersCount4FilterQuery,
      aggregateEmailsDisplay,
      ...composeEmailProps
    },
    previewEmailProps,
    previewSubject:
      type === EMAIL_DIALOG_TYPES.TEMPLATE ? data.subjectInEdit : data.subject,
    previewBody:
      type === EMAIL_DIALOG_TYPES.TEMPLATE ? data.bodyInEdit : data.body,
    showFrequencyTab,
    ...generalDialogProps
  };

  return (
    <Context.Provider value={contextProps}>
      {view === VIEWS.NO_DIALOG ? (
        <EmailNoDialogView {...viewProps} />
      ) : view === VIEWS.DIALOG ? (
        <EmailDialogView {...viewProps} />
      ) : (
        <EmailSimpleView {...viewProps} />
      )}
    </Context.Provider>
  );
};

export default EmailDialogEngine;
